import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { getCarrierLogoSrc } from '@/util/carrierLogo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  FundingSourceCostSlideOutContent,
  FundingSourceLtcPayoutSlideOutContent,
  ROIGuidance,
  ROISlideOutContent,
  SlideOutSheet,
  resetPolicyApplicationStateStatus,
  selectClient,
  selectLastCreatedApplicationId,
  selectPolicyApplications,
  selectPolicyApplicationsStatus,
  selectSession,
} from '@client';
import {
  FundingPolicyType,
  SessionType,
  UnitedStatesState,
  calculateAge,
  formatCurrency,
  zipCodeToState,
} from '@shared';
import { analytics } from '@client/analytics';
import { getProductAvailableStates } from '@shared/carrierStateAvailability';
import { CARRIERS_WITHOUT_APPLICATION_LOGIC } from '@shared/constants';
import { ChevronDown, ChevronRight, Loader, MoreHorizontal } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ContactSupportButton } from '../ContactSupportButton';
import { StateOfResidenceSelector } from '../StateOfResidenceSelector';
import { PartnershipEligibilityDisclaimer } from '../PartnershipEligibilityDisclaimer';
import { PartnershipEligibleBadge } from '../PartnershipEligibleBadge';
import { OutdatedQuoteBadge, OutdatedQuoteText } from '../OutdatedQuoteBadge';
import { PolicyApplicationDeleteModal } from './PolicyApplicationDeleteModal';
import { Badge } from '@design-system';

function getBioPercentFromPolicy(policy: {
  policyInflationProtection?: number | null;
}): number | null {
  if (!policy) return null;
  const pct = policy.policyInflationProtection;
  if (pct == null || pct === 0) return null;
  return Math.round(pct * 100);
}

interface ApplicationCardProps {
  application: ApplicationCardData;
  clientId: string;
  navigateToApplication: (clientPolicyApplicationId: string) => void;
  handleInsertPolicyApplication: (
    clientPolicyApplicationId: string,
    fundingPolicyType: FundingPolicyType,
    fundingSourceId: string,
    stateOfResidenceAbbreviation: string,
  ) => void;
  handleDeletePolicyApplication: (clientPolicyApplicationId: string) => void;
}

const PolicyApplicationDashboardCard: React.FC<ApplicationCardProps> = ({
  application,
  clientId,
  navigateToApplication,
  handleInsertPolicyApplication,
  handleDeletePolicyApplication,
}) => {
  const navigate = useNavigate();
  const policyApplicationStatus = useSelector(selectPolicyApplicationsStatus);
  const policyApplications = useSelector(selectPolicyApplications);
  const lastCreatedApplicationId = useSelector(selectLastCreatedApplicationId);
  const isCreatingPolicyApplication = policyApplicationStatus === 'creatingPolicyApplication';
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const session = useSelector(selectSession);
  const isWizard = session?.sessionType === SessionType.wizard;
  const clientZipCode = client?.intakeSurvey?.clientZipCode;
  const stateFromZipCode = clientZipCode ? zipCodeToState(clientZipCode)?.stateAbbreviation : '';
  const [selectedState, setSelectedState] = useState<string>(stateFromZipCode || '');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Show state selection for not-started applications when expanded
  const showStateSelection =
    (application.status === 'not_created' || application.status === 'not_started') && isExpanded;

  useEffect(() => {
    if (
      policyApplicationStatus === 'creatingPolicyApplicationComplete' &&
      (application.status === 'not_started' || application.status === 'not_created') &&
      lastCreatedApplicationId &&
      lastCreatedApplicationId === application.clientPolicyApplicationId
    ) {
      dispatch(resetPolicyApplicationStateStatus());
      navigateToApplication(lastCreatedApplicationId);
      const newlyCreatedApp = policyApplications.find(
        app =>
          app.fundingSourceId === application.fundingSourceId &&
          app.clientPolicyApplicationId === application.clientPolicyApplicationId,
      );

      if (newlyCreatedApp) {
        navigateToApplication(application.clientPolicyApplicationId);
      }
    }
  }, [
    policyApplicationStatus,
    application.status,
    application.fundingSourceId,
    lastCreatedApplicationId,
    application.clientPolicyApplicationId,
    clientId,
    dispatch,
    navigate,
    navigateToApplication,
    policyApplications,
  ]);

  const getStatusConfig = (status: ApplicationStatus) => {
    switch (status) {
      case 'not_created':
      case 'not_started':
        return {
          label: 'Not Started',
          variant: 'secondary' as const,
          color: 'text-gray-700',
          bgColor: 'bg-gray-100',
          icon: (
            <div className="relative h-5 w-5">
              <div className="absolute inset-0 h-5 w-5 rounded-full border-2 border-gray-300 bg-white" />
              <div className="absolute inset-0 h-5 w-5 rounded-full border-2 border-gray-300 opacity-40" />
            </div>
          ),
        };
      case 'in_progress':
        return {
          label: 'In Progress',
          variant: 'default' as const,
          color: 'text-primary',
          bgColor: 'bg-primary-50',
          icon: (
            <div className="relative h-5 w-5">
              <div className="border-primary bg-primary-100 h-5 w-5 rounded-full border-2" />
              <div className="bg-primary absolute inset-1 h-3 w-3 rounded-full" />
            </div>
          ),
        };
      case 'completed':
      case 'submitted':
        return {
          label: 'Submitted',
          variant: 'default' as const,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          icon: (
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-green-500 shadow-sm">
              <svg
                className="h-3 w-3 text-white"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          ),
        };
      default:
        return {
          label: status,
          variant: 'secondary' as const,
          color: 'text-gray-700',
          bgColor: 'bg-gray-100',
          icon: <div className="h-4 w-4 rounded-full border-2 border-gray-300" />,
        };
    }
  };

  const getActionButton = () => {
    const isApplicationUnsupported =
      !isWizard && CARRIERS_WITHOUT_APPLICATION_LOGIC.includes(application.carrier as any);

    switch (application.status) {
      case 'not_created':
      case 'not_started':
        return isApplicationUnsupported ? (
          <div className="border-primary/20 bg-primary-50 text-primary mb-3 flex w-full items-center justify-between gap-4 rounded-md border p-3 text-sm">
            <div>
              <p className="font-medium">{application.carrier} Applications Launching Soon</p>
              <p className="text-primary-dark/80 mt-1 text-xs">
                We&apos;re putting the finishing touches on application support for{' '}
                {application.carrier} products. You&apos;ll be able to submit applications directly
                through Waterlily by mid-December.
              </p>
            </div>
            <div className="shrink-0">
              <ContactSupportButton
                subject={`Question about ${application.carrier} applications`}
                size="sm"
                variant="outline"
                className="border-primary/30 hover:bg-primary-50 bg-white text-xs"
              >
                Contact Support
              </ContactSupportButton>
            </div>
          </div>
        ) : (
          <Button
            onClick={() => {
              if (application.status === 'not_created') {
                if (!isExpanded) {
                  setIsExpanded(true);
                } else if (selectedState) {
                  analytics.createPlan({
                    clientId,
                    planType: application.fundingPolicyType?.toString(),
                    planId: application.clientPolicyApplicationId,
                  });
                  handleInsertPolicyApplication(
                    application.clientPolicyApplicationId,
                    application.fundingPolicyType,
                    application.fundingSourceId ?? '',
                    selectedState,
                  );
                }
              } else {
                navigateToApplication(application.clientPolicyApplicationId);
              }
            }}
            className={cn(
              'w-full font-semibold shadow-sm transition-all hover:shadow-md sm:w-[180px]',
              isCreatingPolicyApplication && 'cursor-not-allowed opacity-50',
            )}
            disabled={isCreatingPolicyApplication || (isExpanded && !selectedState)}
          >
            {isCreatingPolicyApplication ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : !isExpanded ? (
              'Start Application'
            ) : selectedState ? (
              'Continue to Application'
            ) : (
              'Select State to Continue'
            )}
          </Button>
        );

      case 'in_progress':
        return (
          <Button
            onClick={() => navigateToApplication(application.clientPolicyApplicationId!)}
            variant="outline"
            className="border-primary-300 hover:bg-primary-50 hover:border-primary-400 w-full font-semibold transition-all sm:w-[170px]"
          >
            Resume
          </Button>
        );
      case 'completed':
      case 'submitted':
        return (
          <Button
            onClick={() => navigateToApplication(application.clientPolicyApplicationId!)}
            className="w-full font-semibold sm:w-[170px]"
            variant="outline"
          >
            View Application
          </Button>
        );
      default:
        return null;
    }
  };

  const statusConfig = useMemo(() => getStatusConfig(application.status), [application.status]);
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const maskIfDeactivated = (str: string) => {
    if (application.isDeactivated) return '--';
    return str;
  };
  // An out-of-date quote's figures stay visible but are greyed and struck
  // through: they are not counted in the plan. The card takes a red border.
  const isQuoteOutdated = !!application.quoteOutdatedReason;
  const outdatedValueClass = isQuoteOutdated ? 'text-gray-500 line-through' : undefined;

  return (
    <Card
      className={cn(
        'group relative overflow-hidden rounded-2xl border bg-white transition-all duration-300 hover:shadow-xl',
        isQuoteOutdated
          ? 'border-destructive/50 hover:border-destructive'
          : 'hover:border-primary-300 border-gray-200',
      )}
      data-testid={`application-dashboard-card-${application.carrier.toLowerCase()}`}
    >
      <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      <CardContent className="relative p-4 sm:p-6">
        <div className="space-y-5">
          {/* Header */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex flex-1 items-center gap-2 sm:gap-3">
              <div
                className="shrink-0 rounded-lg bg-white p-1.5 shadow-sm sm:p-2"
                data-testid={`application-dashboard-card-carrier-logo-${application.carrier.toLowerCase()}`}
              >
                <img
                  src={getCarrierLogoSrc(application.carrier)}
                  alt={application.carrier}
                  className="h-6 w-6 object-contain sm:h-8 sm:w-8"
                />
              </div>
              <div className="min-w-0">
                <h3
                  className="text-primary-dark truncate text-base font-semibold sm:text-lg"
                  data-testid={`application-dashboard-card-title-${application.carrier.toLowerCase()}`}
                >
                  {application.title}
                </h3>
                <p
                  className="truncate text-xs text-gray-700 sm:text-sm"
                  data-testid={`application-dashboard-card-carrier-name-${application.carrier.toLowerCase()}`}
                >
                  By {application.carrier}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 sm:justify-end sm:gap-3">
              <div
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium sm:gap-2 sm:text-sm',
                  statusConfig.bgColor,
                )}
                data-testid={`application-dashboard-card-status-${application.carrier.toLowerCase()}`}
              >
                {statusConfig.icon}
                <span className={statusConfig.color}>{statusConfig.label}</span>
                {application.progress && application.status === 'in_progress' ? (
                  <span className={cn('font-semibold', statusConfig.color)}>
                    {application.progress}%
                  </span>
                ) : null}
              </div>
              {/* Only show dropdown menu for applications that have been created */}
              {application.status !== 'not_created' && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="h-7 w-7 p-0 text-gray-700 hover:bg-gray-100 sm:h-8 sm:w-8"
                    >
                      <span className="sr-only">Open menu</span>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => setShowDeleteModal(true)}
                      className="text-red-600 focus:text-red-600"
                    >
                      Clear Progress
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          <OutdatedQuoteBadge
            reason={application.quoteOutdatedReason}
            className="mt-2"
          />

          {client?.intakeSurvey?.clientBirthDate && (
            <PartnershipEligibleBadge
              policyType={application.fundingPolicyType}
              state={selectedState || stateFromZipCode || ''}
              primaryAge={calculateAge(new Date(client.intakeSurvey.clientBirthDate))}
              bioPercent={
                getBioPercentFromPolicy(
                  client?.multipleFundingSources?.ltcPolicy?.[
                    application.fundingSourceId ?? ''
                  ],
                ) ?? 'No BIO'
              }
              isCompoundInflation={
                !client?.multipleFundingSources?.ltcPolicy?.[
                  application.fundingSourceId ?? ''
                ]?.isSimpleInflationProtection
              }
            />
          )}

          <PolicyApplicationDeleteModal
            clientName={client.clientFullName}
            productName={application.title}
            applicationStatus={application.status}
            open={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={() => {
              handleDeletePolicyApplication(application.clientPolicyApplicationId);
              setShowDeleteModal(false);
            }}
          />

          {/* State Selection for not started applications */}
          {showStateSelection && (
            <div className="animate-in slide-in-from-top-2 space-y-4 duration-200">
              <div className="border-t border-gray-200 pt-4">
                <div className="border-primary-200  bg-primary-50 rounded-lg border p-4">
                  <StateOfResidenceSelector
                    value={selectedState}
                    onChange={setSelectedState}
                    disabled={!isWizard}
                    availableStates={getProductAvailableStates(application.fundingPolicyType)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Primary Funding Metrics */}
          {(application.totalCost !== undefined ||
            application.ltcPayout !== undefined ||
            application.projectedReturnOnInvestment !== undefined) && (
            <div className="space-y-3 rounded-lg bg-gray-50 p-4">
              <div className="grid grid-cols-1 gap-3 text-left text-sm sm:grid-cols-3 sm:gap-4 sm:text-center">
                {application.totalCost !== undefined && (
                  <div className="text-center">
                    <div className="text-xs uppercase tracking-wide text-gray-700">
                      Modeled Cost
                    </div>
                    <div className="flex items-center justify-center gap-1 text-lg font-semibold">
                      <SlideOutSheet
                        title="FAQ"
                        slideOutContent={<FundingSourceCostSlideOutContent />}
                        maxWidth="custom-roi"
                      >
                        <span className={cn('text-red-600 hover:underline', outdatedValueClass)}>
                          {formatCurrency(application.totalCost, 2)}
                        </span>
                      </SlideOutSheet>
                      <ROIGuidance className="w-max p-0.5 [&_svg]:!size-3 [&_svg]:p-0" />
                    </div>
                  </div>
                )}
                {application.ltcPayout !== undefined && (
                  <div className="text-center">
                    <div className="text-xs uppercase tracking-wide text-gray-700">
                      Modeled LTC Payout
                    </div>
                    <div className="flex items-center justify-center gap-1 text-lg font-semibold">
                      <SlideOutSheet
                        title="FAQ"
                        slideOutContent={<FundingSourceLtcPayoutSlideOutContent />}
                        maxWidth="custom-roi"
                      >
                        <span className={cn('text-green-600 hover:underline', outdatedValueClass)}>
                          {maskIfDeactivated(formatCurrency(application.ltcPayout, 2))}
                        </span>
                      </SlideOutSheet>
                      <ROIGuidance className="w-max p-0.5 [&_svg]:!size-3 [&_svg]:p-0" />
                    </div>
                  </div>
                )}
                {application.formattedROI && (
                  <div className="text-center">
                    <div className="text-xs uppercase tracking-wide text-gray-700">
                      {application.roiCalculationType === 'compoundAnnualGrowthRate'
                        ? 'Modeled Annual ROI'
                        : 'Modeled Total ROI'}
                    </div>
                    <div className="text-primary flex items-center justify-center gap-1 text-lg font-semibold">
                      {application.quoteOutdatedReason ? (
                        <OutdatedQuoteText reason={application.quoteOutdatedReason} />
                      ) : (
                        <SlideOutSheet
                          title="FAQ"
                          slideOutContent={<ROISlideOutContent />}
                          maxWidth="custom-roi"
                        >
                          <span className="hover:underline">
                            {maskIfDeactivated(application.formattedROI)}
                          </span>
                        </SlideOutSheet>
                      )}
                      <ROIGuidance className="w-max p-0.5 [&_svg]:!size-3 [&_svg]:p-0" />
                    </div>
                  </div>
                )}
              </div>

              {/* Details Dropdown */}
              {(application.nonLtcPayout !== undefined ||
                application.totalPayout !== undefined) && (
                <div className="border-t border-gray-200 pt-3">
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="flex w-full items-center justify-between text-sm text-gray-700 transition-colors hover:text-gray-800"
                  >
                    <span className="font-medium">Additional Details</span>
                    {showDetails ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>

                  {showDetails && (
                    <div className="animate-in slide-in-from-top-2 mt-3 space-y-2 text-sm duration-200">
                      {application.nonLtcPayout !== undefined && (
                        <div className="flex justify-between">
                          <span className="text-gray-700">Non-LTC Payout:</span>
                          <span className={cn('font-semibold text-gray-900', outdatedValueClass)}>
                            {maskIfDeactivated(formatCurrency(application.nonLtcPayout, 2))}
                          </span>
                        </div>
                      )}
                      {application.totalPayout !== undefined && (
                        <div className="flex justify-between">
                          <span className="text-gray-700">Total Payout:</span>
                          <span className={cn('font-semibold text-gray-900', outdatedValueClass)}>
                            {maskIfDeactivated(formatCurrency(application.totalPayout, 2))}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {application.fundingPolicyType === FundingPolicyType.careScoutCareAssurance && (
                <div className="mt-3 space-y-3">
                  <div className="rounded-md border border-blue-100 bg-blue-50 p-3 text-xs text-gray-700">
                    <p>
                      Please note that the Return on Investment (ROI) and CAGR projections provided
                      by Waterlily for the Care Assurance policy include modeled savings from the
                      CareScout Quality Network (CQN). Access to the CQN is a non-policy benefit, is
                      not guaranteed by the insurance contract, and may be modified or discontinued
                      by CareScout at any time. Actual savings and network availability will vary.
                    </p>
                  </div>
                  {client?.intakeSurvey?.clientBirthDate && (
                    <PartnershipEligibilityDisclaimer
                      policyType={application.fundingPolicyType}
                      state={selectedState || stateFromZipCode || ''}
                      primaryAge={calculateAge(new Date(client.intakeSurvey.clientBirthDate))}
                      bioPercent={
                        getBioPercentFromPolicy(
                          client?.multipleFundingSources?.ltcPolicy?.[
                            application.fundingSourceId ?? ''
                          ],
                        ) ?? 'No BIO'
                      }
                      isCompoundInflation={
                        !client?.multipleFundingSources?.ltcPolicy?.[
                          application.fundingSourceId ?? ''
                        ]?.isSimpleInflationProtection
                      }
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Progress Bar for in-progress applications */}
          {application.status === 'in_progress' && application.progress !== undefined && (
            <div className="space-y-2">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                <div
                  className="bg-primary-500 h-full rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${application.progress}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-700">
                <span>Progress</span>
                <span className="text-primary-600 font-semibold">
                  {application.progress}% Complete
                </span>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
            {application.stateOfResidenceAbbreviation && (
              <Badge>
                {UnitedStatesState[application.stateOfResidenceAbbreviation]?.stateName ||
                  application.stateOfResidenceAbbreviation}
              </Badge>
            )}
            <div className="ml-auto">{getActionButton()}</div>
          </div>
          {isWizard && application.lastUpdated && (
            <div className="bg-muted mt-2 flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm">
              <span>Last Updated:</span>
              <span>{formatDate(application.lastUpdated)}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PolicyApplicationDashboardCard;
